import { Button, Card, H3, Menu, MenuItem } from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import { Popover2 } from '@blueprintjs/popover2';
import classNames from 'classnames';
import * as React from 'react';
import { Link } from 'react-router-dom';

import { projectsActionCreators } from 'src/actions/projects/actionCreators';
import { EditableLabel } from 'src/components/EditableLabel';
import {
  TEST_IDS,
  getProjectCardTestId,
  getProjectMenuButtonTestId,
  getProjectTitleTestId,
} from 'src/constants/testIds';
import {
  getDefaultProjectDataPrefix,
  getDefaultProjectModelPrefix,
} from 'src/containers/ProjectDiagram/utils/generateProjectIri';
import { Project } from 'src/store/state/projects';
import * as copy from 'src/templates/copy';
import { formatDateFromMs } from 'src/utils/formatDateFromMs';
import { generateDateTimeId } from 'src/utils/generateId';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';
import { getProjectRoute } from 'src/utils/routes/getProjectRoute';

type ProjectCardProps = {
  idx: number | undefined;
  hidden?: boolean;
  project: Project;
};

export const ProjectCard: React.VFC<ProjectCardProps> = ({
  idx,
  hidden,
  project,
}) => {
  const dispatch = useReduxDispatch();
  const [isHovered, setIsHovered] = React.useState(false);
  const [isMenuOpen, setisMenuOpen] = React.useState(false);

  const handleMouseEnter = React.useCallback(() => {
    setIsHovered(true);
  }, [setIsHovered]);

  const handleMouseLeave = React.useCallback(() => {
    setIsHovered(false);
  }, [setIsHovered]);

  const handleRename = (name: string) => {
    dispatch(projectsActionCreators.renamedProject({ id: project.id, name }));
    if (project.modelNamespace === getDefaultProjectModelPrefix(project)) {
      const modelNamespace = getDefaultProjectModelPrefix({ ...project, name });
      dispatch(
        projectsActionCreators.updatedProjectModelNamespace(
          project.id,
          modelNamespace
        )
      );
    }
    if (project.dataNamespace === getDefaultProjectDataPrefix(project)) {
      const dataNamespace = getDefaultProjectDataPrefix({
        ...project,
        name,
      });
      dispatch(
        projectsActionCreators.updatedProjectDataNamespace(
          project.id,
          dataNamespace
        )
      );
    }
  };

  const handleDuplicateProject = React.useCallback(() => {
    const copyId = generateDateTimeId();
    dispatch(projectsActionCreators.duplicatedProject(project.id, copyId));
  }, [dispatch, project]);

  const handleRemoveProject = React.useCallback(() => {
    const restoreAction = projectsActionCreators.restoredProject({
      id: project.id,
      name: project.name,
    });
    dispatch(
      projectsActionCreators.removedProject<typeof restoreAction>({
        id: project.id,
        name: project.name,
        restoreAction,
      })
    );
  }, [dispatch, project]);

  const formattedDateString = React.useMemo(() => {
    return formatDateFromMs(project.dateLastUpdated);
  }, [project.dateLastUpdated]);

  return (
    <Link
      className={classNames('sd-link-reset', {
        'sd-display-none-important': hidden,
      })}
      data-testid={getProjectCardTestId(project.id, idx)}
      to={getProjectRoute(project.id)}
    >
      <Card
        className="sd-project-card"
        interactive
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <div className="sd-project-card__header">
          <H3
            className="sd-project-card__name"
            data-testid={getProjectTitleTestId(project.id, idx)}
          >
            {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
            <span
              onClick={(e) => {
                e.preventDefault();
              }}
            >
              <EditableLabel onSetValue={handleRename} value={project.name} />
            </span>
          </H3>
          <Popover2
            content={
              <Menu>
                <MenuItem
                  data-testid={TEST_IDS.projectMenu.duplicateItem}
                  onClick={handleDuplicateProject}
                  text={copy.components.home.cards.duplicateMenuItem}
                />
                <MenuItem
                  data-testid={TEST_IDS.projectMenu.deleteItem}
                  onClick={handleRemoveProject}
                  text={copy.components.home.cards.deleteMenuItem}
                />
              </Menu>
            }
            isOpen={isMenuOpen}
            minimal
            onClose={(e) => {
              // react testing library does not provide event here
              if (e) {
                // prevent triggering card link when clicking menu items
                e.stopPropagation();
              }
              setisMenuOpen(false);
            }}
            targetProps={{
              onClick: (e) => {
                // prevent triggering parent interaction
                e.preventDefault();
              },
            }}
          >
            <Button
              active={isMenuOpen}
              data-testid={getProjectMenuButtonTestId(project.id, idx)}
              icon={isHovered || isMenuOpen ? IconNames.MORE : IconNames.BLANK}
              minimal
              onClick={() => {
                setisMenuOpen(!isMenuOpen);
              }}
            />
          </Popover2>
        </div>
        <div
          className="sd-project-card__update-date"
          data-testid={TEST_IDS.projectCard.updateDate}
        >
          Last updated: {formattedDateString}
        </div>
      </Card>
    </Link>
  );
};
